import http, { client, type HttpRequestOptions } from 'utils/http';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import { getCookie } from 'utils/helperFunctions';
import {
  getOnboardingFormsUrl,
  getOnboardingFormUrl,
  getOnboardingFormHistoriesUrl,
  getOnboardingFormLoginUrl,
  getPublicOnboardingFormUrl,
  getPublicCountriesUrl,
  getPublicStatesUrl,
  getPublicAvailableIntegrationsUrl,
  getValidateIntegrationCredentialsUrl,
  getOauthConfigurationUrl,
  getApproveOnboardingFormUrl,
  getBusinessAutocompleteUrl,
  getBusinessPlaceDetailsUrl,
  getPublicOnboardingFormsUrl,
  getCheckoutSessionUrl,
  getCreatePromoCodeUrl,
  getPublicOnboardingPostConfigurationsUrl,
  getAgencyOnboardingPostConfigurationsUrl
} from './apiUrls';

import {
  OnboardingFormRequest,
  OnboardingFormResponse,
  HistoryLog,
  BusinessAutocompleteResponse,
  BusinessPlaceDetails,
  PublicOnboardingFormRequest
} from './types';

// LIST (pagination)
export function getOnboardingForms(
  page: number,
  filters?: Record<string, any>
): Promise<{ data: OnboardingFormResponse[]; meta: MetaApiResponse }> {
  return http.get(getOnboardingFormsUrl(), { page, ...filters });
}

// GET SINGLE
export function getOnboardingForm(id: string): Promise<OnboardingFormResponse> {
  return http.get(getOnboardingFormUrl(id));
}

// CREATE
export function createOnboardingForm(payload: OnboardingFormRequest | FormData): Promise<OnboardingFormResponse> {
  return http.post(getOnboardingFormsUrl(), payload);
}

// CREATE PUBLIC
export function createPublicOnboardingForm(
  payload: PublicOnboardingFormRequest | FormData
): Promise<OnboardingFormResponse> {
  return http.post(getPublicOnboardingFormsUrl(), payload);
}

// UPDATE
export function updateOnboardingForm(
  id: string,
  payload: OnboardingFormRequest | FormData,
  options?: HttpRequestOptions
): Promise<OnboardingFormResponse> {
  return http.patch(getOnboardingFormUrl(id), payload, options);
}

// ARCHIVE (soft delete)
export function archiveOnboardingForm(id: string): Promise<{ meta: { message: string } }> {
  return http.archive(getOnboardingFormUrl(id));
}

// GET HISTORIES
export function getOnboardingFormHistories(id: string): Promise<{ data: HistoryLog[]; links?: any; meta?: any }> {
  return http.get(getOnboardingFormHistoriesUrl(id));
}

// PUBLIC LOGIN
export interface OnboardingFormLoginRequest {
  email?: string;
  phone_number?: string;
  token?: string;
  update_message?: string;
}

export interface OnboardingFormLoginResponse {
  id?: string | number | null;
  token?: string;
  form_token?: string;
  message?: string;
  onboarding_config?: {
    id?: string | number | null;
    stage?: string | number | null;
    step?: string | null;
    campaign_type?: string | null;
    campaign_expires_at?: string | null;
  };
}

export function loginOnboardingForm(payload: OnboardingFormLoginRequest): Promise<OnboardingFormLoginResponse> {
  return http.post(getOnboardingFormLoginUrl(), payload);
}

// GET PUBLIC ONBOARDING FORM (with token from cookies)
export function getPublicOnboardingForm(id: string): Promise<OnboardingFormResponse> {
  const onboardToken = getCookie('token') || getCookie('auth_token') || '';
  return client({
    url: getPublicOnboardingFormUrl(id),
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Onboard-Token': onboardToken || ''
    },
    params: { include: 'pricing_details' },
    withCredentials: false
  }).then((response: any) => response?.data);
}

// UPDATE PUBLIC ONBOARDING FORM (with token from cookies)
export function updatePublicOnboardingForm(
  id: string,
  payload: OnboardingFormRequest | FormData,
  options?: HttpRequestOptions
): Promise<OnboardingFormResponse> {
  const onboardToken = getCookie('token') || getCookie('auth_token') || '';

  const isFormData = payload instanceof FormData;

  return client({
    url: getPublicOnboardingFormUrl(id),
    method: 'PUT',
    data: payload,
    headers: {
      'Content-Type': isFormData ? 'multipart/form-data' : 'application/json',
      Accept: 'application/json',
      'X-Onboard-Token': onboardToken || ''
    },
    withCredentials: false,
    skipSuccessToast: options?.skipSuccessToast
  } as any).then((response: any) => response?.data);
}

// GET PUBLIC COUNTRIES
export function getPublicCountries(): Promise<{ value: string | number; label: string }[]> {
  return client({
    url: getPublicCountriesUrl(),
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    withCredentials: false
  }).then((response: any) => {
    const data = response?.data || {};
    return Object.keys(data).map((key) => ({
      value: key,
      label: data[key]
    }));
  });
}

// GET PUBLIC STATES
export function getPublicStates(countryCode: string): Promise<{ value: string | number; label: string }[]> {
  return client({
    url: getPublicStatesUrl(countryCode),
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    withCredentials: false
  }).then((response: any) => {
    const data = response?.data || {};
    return Object.keys(data).map((key) => ({
      value: key,
      label: data[key]
    }));
  });
}

// GET PUBLIC AVAILABLE INTEGRATIONS
export interface PublicAvailableIntegrationResponse {
  id: number;
  name: string;
  code: string;
  description?: string;
  avatar_url?: string;
  industries?: string[];
  connection_type?: string;
  integration_name?: string;
  pre_configurations?: any;
  post_configuration_required?: boolean;
  article_id?: string | number | null;
}

export function getPublicAvailableIntegrations(
  params?: Record<string, any>
): Promise<{ data: PublicAvailableIntegrationResponse[]; meta?: any }> {
  return client({
    url: getPublicAvailableIntegrationsUrl(),
    method: 'GET',
    params,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    withCredentials: false
  }).then((response: any) => response?.data || { data: [] });
}

export function validateIntegrationCredentials(payload: {
  integration: { integration_name: string; configuration: Record<string, unknown> };
}): Promise<{
  success: boolean;
  active: boolean;
  requires_oauth?: boolean;
  oauth_url?: string;
  manual_validation_required?: boolean;
  message?: string;
}> {
  return client({
    url: getValidateIntegrationCredentialsUrl(),
    method: 'POST',
    data: payload,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    withCredentials: false
  }).then((response: any) => response?.data);
}

export function getOnboardingPostConfigurationFields({
  code,
  configuration,
  isPublicRoute
}: {
  code: string;
  configuration: Record<string, unknown>;
  isPublicRoute: boolean;
}): Promise<any> {
  const params = {
    code,
    configuration
  };

  if (isPublicRoute) {
    const onboardToken = getCookie('token') || getCookie('auth_token') || '';
    return client({
      url: getPublicOnboardingPostConfigurationsUrl(),
      method: 'GET',
      params,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Onboard-Token': onboardToken || ''
      },
      withCredentials: false
    }).then((response: any) => response?.data);
  }

  return http.get(getAgencyOnboardingPostConfigurationsUrl(), params);
}

export function getOauthConfiguration(oauthConfigurationToken: string): Promise<{
  configuration: Record<string, unknown>;
}> {
  return client({
    url: getOauthConfigurationUrl(),
    method: 'GET',
    params: { oauth_configuration_token: oauthConfigurationToken },
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    withCredentials: false
  }).then((response: any) => response?.data);
}

// APPROVE ONBOARDING FORM
export function approveOnboardingForm(id: string): Promise<OnboardingFormResponse> {
  return http.post(getApproveOnboardingFormUrl(id));
}

// BUSINESS AUTOCOMPLETE
export function getBusinessAutocomplete(input: string): Promise<BusinessAutocompleteResponse> {
  const subdomain = window.location.hostname.split('.')[0];
  return http.get(getBusinessAutocompleteUrl(), { input, subdomain });
}

// BUSINESS PLACE DETAILS
export function getBusinessPlaceDetails(place_id: string): Promise<BusinessPlaceDetails> {
  const subdomain = window.location.hostname.split('.')[0];
  return http.get(getBusinessPlaceDetailsUrl(), { place_id, subdomain });
}

export function createCheckoutSession(payload: any): Promise<{ checkout_url: string }> {
  return http.post(getCheckoutSessionUrl(), payload);
}

// CREATE PROMO CODE
export function createPromoCode(id: string): Promise<any> {
  return http.get(getCreatePromoCodeUrl(id));
}
